/**
 * src/lib/api/procurement.js — Procurement module client API
 * Talks to /api/procurement (GET for reads, POST for writes)
 */

import { apiFetch, API_BASE } from './client';

const PROCUREMENT = `${API_BASE}/data`;

// ── Purchase Orders ─────────────────────────────────────────────────

export async function fetchPurchaseOrders(filters = {}) {
  const params = new URLSearchParams({ action: 'procurement.orders' });
  if (filters.status) params.set('status', filters.status);
  if (filters.project) params.set('project', filters.project);
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data?.orders || [];
}

// Fetch invoices linked to a purchase order (read-only)
export async function fetchPOInvoices(po_id) {
  const params = new URLSearchParams({ action: 'procurement.po_invoices', po_id });
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data?.invoices || [];
}

export async function createPurchaseOrder({ project_name, supplier_id, notes, line_items }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.create_order',
      project_name,
      supplier_id,
      notes,
      line_items,
    }),
  });
}

export async function updatePOStatus(id, status) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_order_status', id, status }),
  });
}

// Soft-delete — draft/cancelled POs only (see handleArchiveOrder).
export async function archivePurchaseOrder(id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.archive_order', id }),
  });
}

// ── Line Items ──────────────────────────────────────────────────────

export async function updateLineItems(po_id, items) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_line_items', po_id, items }),
  });
}

export async function addLineItem(po_id, item) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.add_line_item', po_id, ...item }),
  });
}

// ── Suppliers ───────────────────────────────────────────────────────

export async function fetchSuppliers() {
  const data = await apiFetch(`${PROCUREMENT}?action=procurement.suppliers`);
  return data?.suppliers || [];
}

export async function createSupplier(supplier) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.create_supplier', ...supplier }),
  });
}

export async function updateSupplier(id, supplier) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_supplier', id, ...supplier }),
  });
}

export async function bulkCreateSuppliers(suppliers) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.bulk_create_suppliers', suppliers }),
  });
}

// ── Supplier Contacts (1:N — TICKET-017) ────────────────────────────

export async function fetchSupplierContacts(supplier_id) {
  const data = await apiFetch(`${PROCUREMENT}?action=procurement.supplier_contacts&supplier_id=${encodeURIComponent(supplier_id)}`);
  return data?.contacts || [];
}

export async function createSupplierContact(contact) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.create_supplier_contact', ...contact }),
  });
}

export async function updateSupplierContact(id, contact) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_supplier_contact', id, ...contact }),
  });
}

export async function deleteSupplierContact(id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.delete_supplier_contact', id }),
  });
}

// ── Supplier Detail (S2) ────────────────────────────────────────────
// Fetches a supplier + related invoices, delivery notes, POs, contacts
export async function fetchSupplierDetail(supplier_id) {
  const params = new URLSearchParams({ action: 'procurement.supplier_detail', supplier_id });
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data;
}

// ── Materials Catalog ───────────────────────────────────────────────

// Server-side catalog search. The server searches name + description + sku +
// internal_sku and can also filter by category and supplier (default supplier
// OR any supplier_sku link). Passing only a query keeps backward compatibility.
export async function searchCatalog(query = '', opts = {}) {
  const params = new URLSearchParams({ action: 'procurement.catalog' });
  if (query) params.set('q', query);
  if (opts.materialId) params.set('material_id', opts.materialId);
  if (opts.category && opts.category !== 'All') params.set('category', opts.category);
  if (opts.supplierId) params.set('supplier_id', opts.supplierId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data?.catalog || [];
}

// Read-only lookup over the PARSED SUPPLIER TARIFFS (`supplier_catalog_items`,
// 15,355 rows from the 2026-06-21 ingest). Separate from searchCatalog because
// these are reference rows with no materials_catalog id — nothing can be added
// to a cart, a PO or stock from here, only read. Returns [] for an empty query
// (the table is far too big to dump). TICKET-327.
export async function searchSupplierTariff(query = '', opts = {}) {
  if (!String(query || '').trim()) return [];
  const params = new URLSearchParams({ action: 'procurement.tariff_search', q: query });
  if (opts.supplier) params.set('supplier', opts.supplier);
  if (opts.limit) params.set('limit', String(opts.limit));
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data?.tariff || [];
}

// Semantic (AI) catalogue search over the procurement-items Chroma collection
// (materials + invoice lines + BOM history, 1536d). Answers NL questions like
// "items used on biminis?". Returns [{ id, text, metadata, distance }].
export async function procurementSemanticSearch(query, opts = {}) {
  const params = new URLSearchParams({ action: 'procurement.semantic_search', q: query });
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.entityType) params.set('entity_type', opts.entityType);
  if (opts.jobType) params.set('job_type', opts.jobType);
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data?.results || [];
}

// Virtual picking: given a job-type ("bimini"), RAG-retrieve the materials
// historically used/purchased → a ranked candidate pick-list (catalog-enriched).
export async function procurementVirtualPick(job, opts = {}) {
  const params = new URLSearchParams({ action: 'procurement.virtual_pick', job });
  if (opts.limit) params.set('limit', String(opts.limit));
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data || { job, picks: [], available: false };
}

// Purchase intelligence over material_purchases: by-supplier, last-bought-date
// ranking, spend, supersession suggestions. opts: { supplierId, materialId,
// sort: 'last_bought'|'spend'|'price'|'name', since (ISO date), limit }.
export async function fetchPurchases(opts = {}) {
  const params = new URLSearchParams({ action: 'procurement.purchases' });
  if (opts.supplierId) params.set('supplier_id', opts.supplierId);
  if (opts.materialId) params.set('material_id', opts.materialId);
  if (opts.sort) params.set('sort', opts.sort);
  if (opts.since) params.set('since', opts.since);
  if (opts.limit) params.set('limit', String(opts.limit));
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data || { items: [], suppliers_seen: [] };
}

export async function addCatalogItem(item) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.add_catalog_item', ...item }),
  });
}

export async function updateMaterial(id, fields) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_material', id, ...fields }),
  });
}

// ── Supplier SKUs ───────────────────────────────────────────────────

// TICKET-804 — everything the material/SKU detail sheet needs, in ONE call:
// the material row, every image (presigned), the supplier options (from
// v_material_supplier_options, with price rank and gap-to-best) and the
// suggested same-part equivalents from suppliers this material does not have.
export async function fetchMaterialDetail(material_id) {
  if (!material_id) return null;
  const params = new URLSearchParams({ action: 'procurement.material_detail', material_id });
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  if (!data || data.error) return null;
  return {
    material: data.material || null,
    images: Array.isArray(data.images) ? data.images : [],
    supplier_options: Array.isArray(data.supplier_options) ? data.supplier_options : [],
    equivalents: Array.isArray(data.equivalents) ? data.equivalents : [],
  };
}

// ── Material photos (TICKET-811) ────────────────────────────────────
//
// The gallery's write half. Reads come back with fetchMaterialDetail.
//
// Two steps on purpose: the BYTES go straight to MinIO through a presigned PUT
// (they never pass through our API), and only then does a small JSON call record
// the row. That is the same shape the voice and document uploads already use, and
// it is why a 20 MB photo does not have to fit in a serverless request body.

/** Presigned PUT for one material photo. The SERVER picks the object key. */
export async function presignMaterialImage({ materialId, sha256, fileName, mimeType, size }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.material_image_presign',
      material_id: materialId,
      content_sha256: sha256,
      file_name: fileName,
      mime_type: mimeType,
      file_size_bytes: size,
    }),
  });
}

/** Record a photo whose bytes are already in MinIO. Idempotent on sha256. */
export async function registerMaterialImage({ materialId, sha256, objectKey, bucket, width, height, caption }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.material_image',
      op: 'register',
      material_id: materialId,
      content_sha256: sha256,
      object_key: objectKey,
      bucket,
      width_px: width,
      height_px: height,
      caption,
    }),
  });
}

/** "This is the one" — the photo that represents the material on tiles and BOM rows. */
export async function setPrimaryMaterialImage(materialId, imageId) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.material_image', op: 'set_primary', material_id: materialId, image_id: imageId }),
  });
}

/** Soft-retire a photo. Never a delete — the object stays where it is. */
export async function archiveMaterialImage(materialId, imageId) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.material_image', op: 'archive', material_id: materialId, image_id: imageId }),
  });
}

export async function fetchSupplierSkus(material_id) {
  const data = await apiFetch(`${PROCUREMENT}?action=procurement.supplier_skus&material_id=${material_id}`);
  return data?.supplier_skus || [];
}

export async function upsertSupplierSku(sku) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.upsert_supplier_sku', ...sku }),
  });
}

export async function deleteSupplierSku(id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.delete_supplier_sku', id }),
  });
}

// ── QR Scan Reorder (TICKET-228 S3a) ─────────────────────────────────
// reorderScan: new contract w/ idempotent increment, archived supplier skip, spine emit
// reorderFromScan: legacy wrapper (backward compat)

export async function reorderScan({ material_id, sku, qty, source = 'qr' }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.reorder_scan', material_id, sku, qty, source }),
  });
}

export async function reorderFromScan(material_id, qty) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.reorder_from_scan', material_id, qty }),
  });
}

// ── Task Materials (C6) ────────────────────────────────────────────────

export async function fetchTaskMaterials(task_id) {
  const res = await apiFetch(`${PROCUREMENT}?action=procurement.task_materials&task_id=${task_id}`);
  return res?.materials || [];
}

// ── Material Requests (C5) ─────────────────────────────────────────────

export async function fetchMaterialRequests(filters = {}) {
  const params = new URLSearchParams({ action: 'procurement.material_requests' });
  if (filters.status) params.set('status', filters.status);
  if (filters.task_id) params.set('task_id', filters.task_id);
  const res = await apiFetch(`${PROCUREMENT}?${params}`);
  return res?.requests || [];
}

export async function createMaterialRequest(data) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.create_material_request', ...data }),
  });
}

export async function updateMaterialRequest(id, updates) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({ action: 'procurement.update_material_request', id, ...updates }),
  });
}

// TICKET-031.4: group selected pending requests by supplier into a basket.
// Server resolves supplier_id from majority-vote across requests if not given.
export async function createMaterialRequestGroup({ request_ids, supplier_id, notes }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.create_request_group',
      request_ids,
      supplier_id: supplier_id || undefined,
      notes: notes || undefined,
    }),
  });
}

// ── S1 Supplier Dedup (auto + review) ────────────────────────────────

export async function dedupScan() {
  const params = new URLSearchParams({ action: 'procurement.dedup_scan' });
  const data = await apiFetch(`${PROCUREMENT}?${params}`);
  return data || { pairs: [] };
}

export async function mergeSuppliersApi(survivor_id, dupe_id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.merge_suppliers',
      survivor_id,
      dupe_id,
    }),
  });
}

export async function dismissDedupPair(supplier_a, supplier_b) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.dismiss_dedup_pair',
      supplier_a,
      supplier_b,
    }),
  });
}

export async function cartCheckout(items) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.cart_checkout',
      items,
    }),
  });
}

export async function sendPO({ po_id, to_email, message }) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.send_po',
      po_id,
      to_email,
      message,
    }),
  });
}

/**
 * material_vision_describe — extract photo metadata via vision LLM.
 * Called after photo upload. Returns { vision_description, vision_pills_suggested }.
 */
export async function materialVisionDescribe(material_id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.material_vision_describe',
      material_id,
    }),
  });
}

/**
 * material_confirm_pills — user confirms suggested pills → save to tags.
 * Validates and deduplicates against existing tags. Returns { tags }.
 */
export async function materialConfirmPills(material_id, tags) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.material_confirm_pills',
      material_id,
      tags,
    }),
  });
}

// ── S5 TICKET-228: Stock acceptance & reconciliation ──────────────────

/**
 * acceptDeliveryLines — accept delivery note lines → stock intake
 * RPC-called, transactional: stock delta + PO receipt + gap alerts.
 */
export async function acceptDeliveryLines(delivery_note_id, line_ids) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.accept_delivery_lines',
      delivery_note_id,
      line_ids,
    }),
  });
}

/**
 * stockAdjust — manual stock adjustment (CEO/admin only).
 */
export async function stockAdjust(material_id, delta, reason) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    // TICKET-1367: not idempotent (qty + delta). apiFetch retries a 5xx POST once, which
    // applied the delta twice; a stock adjustment is never retried automatically.
    noRetry: true,
    body: JSON.stringify({
      action: 'procurement.stock_adjust',
      material_id,
      delta,
      reason,
    }),
  });
}

/**
 * linkDeliveryInvoice — set supplier_invoice_id on a delivery note.
 */
export async function linkDeliveryInvoice(delivery_note_id, supplier_invoice_id) {
  return apiFetch(PROCUREMENT, {
    method: 'POST',
    body: JSON.stringify({
      action: 'procurement.link_delivery_invoice',
      delivery_note_id,
      supplier_invoice_id,
    }),
  });
}

// ── Supplier Network Graph ──────────────────────────────────────────

export async function fetchProcurementGraph() {
  const data = await apiFetch(`${PROCUREMENT}?action=procurement.graph`);
  return data || { nodes: [], edges: [], meta: {} };
}

// ── Files ───────────────────────────────────────────────────────────

/**
 * presignFileUrl — mint a short-lived signed URL for a stored file (e.g. an
 * invoice PDF) so the client can open it directly. GET returns { url };
 * resolves to the URL string, or null on failure (apiFetch GET returns null).
 */
export async function presignFileUrl(file_id) {
  const data = await apiFetch(`${PROCUREMENT}?action=files.presign_url&file_id=${encodeURIComponent(file_id)}`);
  return data?.url || null;
}
